// @flow
import React from 'react'
import classNames from 'classnames'
import I18n from 'i18n-react'
import ScrollLock from 'react-scrolllock'
import isMobile from 'is-mobile'

// components
import { EmptyComponent } from '../Components'
import filterComponents from '../Components/_filterComponents/'

//types
import type { FilterBlockType, FilterDataType, FilterBlockOptionType } from '../Core/Redux/Types/FilterStateType'

type FilterProps = {
  className: string,
  data: FilterDataType,
  dataCount: number,
  filterBlocks: Array<FilterBlockType>,
  functionalData: { [key: string]: any },
  mobileFilter?: boolean,
  onChange: (blockName: string, name: string, checked: boolean | string) => void,
  onRecalculate: () => void,
  onToggleModal: () => void,
  onToggleTextMode: () => void,
  renderMiniApp: (string) => Object,
  scuData: Object,
  showFilterPush: boolean,
  showRecalculate?: boolean,
  showRecalculateAfter?: string,
  textMode?: boolean,
}

type FilterState = {
  showMobileFilter: boolean,
}

export default class CarFilter extends React.Component<FilterProps, FilterState> {
  static defaultProps = {
    className: '',
    mobileFilter: false,
    onToggleModal: () => {},
    onToggleTextMode: () => {},
    renderMiniApp: () => null,
    textMode: false,
    scuData: {},
    disabled: false,
  }

  state = {
    showMobileFilter: false,
  }

  shouldComponentUpdate(nextProps: FilterProps, nextState: FilterState) {
    return (
      JSON.stringify(nextProps) !== JSON.stringify(this.props) ||
      this.state.showMobileFilter !== nextState.showMobileFilter
    )
  }

  handleOnChange = (blockName: string, key: string, checked: boolean | string) => {
    this.props.onChange(blockName, key, checked)
  }

  handleToggleTextMode = () => {
    this.props.onToggleTextMode()
  }

  handleToggleShowModal = () => {
    this.props.onToggleModal()
    this.setState({ showMobileFilter: !this.state.showMobileFilter })
  }

  handleRecalculateTab = () => {
    this.props.onRecalculate()
  }

  render = () => {
    const {
      className,
      data,
      dataCount,
      filterBlocks,
      functionalData,
      mobileFilter,
      onRecalculate,
      renderMiniApp,
      showFilterPush,
      showMobileFilter,
      showRecalculate,
      showRecalculateAfter,
      textMode,
      disabled,
    } = this.props

    const classes = ['card flex-row flex-wrap px-4 py-3 px-md-3 py-md-0 h-sm-auto', className]
    if (mobileFilter) {
      classes.push('mobile-fullscreen d-sm-none')
    } else {
      classes.push('d-none d-sm-flex')
    }
    if (mobileFilter && !showFilterPush) {
      classes.push('d-none')
    }
    return [
      !mobileFilter && (
        <strong className="d-none d-sm-block mb-1" key="filter-head">
          {I18n.translate('filter.insuranceCount', { context: dataCount })}
        </strong>
      ),
      <div className={classNames(classes)} key="filter-content">
        <div className="mobile-header d-sm-none">
          <span
            className="close-btn"
            onClick={() => {
              this.handleToggleShowModal()
            }}
          >
            <i className="pfp pfp-close" />
          </span>
          <h3>{I18n.translate('filter.changeFilterParameters')}</h3>
          <span>{I18n.translate('filter.insuranceCountMobile', { context: dataCount })}</span>
        </div>
        {filterBlocks.map((filterBlock: FilterBlockType, blockIndex) => {
          const hidden = textMode && (!data[filterBlock.name] || Object.keys(data[filterBlock.name]).length === 0)
          const blockClasses = [
            'd-xl-block align-items-center mb-3 mb-lg-3 w-100',
            {
              'd-none': hidden,
              'd-md-flex': !hidden,
            },
          ]
          if (blockIndex === 0) {
            blockClasses.push('pt-3')
          }
          return [
            blockIndex > 0 && <hr className="mt-0 w-100" key={`${filterBlock.title}-hr`} />,
            filterBlock.miniApp &&
              renderMiniApp(filterBlock.name, { showRecalculate, showRecalculateAfter, onRecalculate }),
            !filterBlock.miniApp && (
              <div className={classNames(blockClasses)} key={filterBlock.title}>
                {textMode ? (
                  <span className="mr-2">{I18n.translate(`filter.block.${filterBlock.title}`)}:</span>
                ) : (
                  <strong className="d-block mt-2 mb-1 mt-md-0 mb-md-0 mr-4">
                    {I18n.translate(`filter.block.${filterBlock.title}`)}
                    {filterBlock.subTitle && [
                      <br key="br" />,
                      <small key="small">{I18n.translate(`filter.block.${filterBlock.subTitle}`)}</small>,
                    ]}
                  </strong>
                )}
                <div className="row w-100">
                  {filterBlock.options.map((filterItem: FilterBlockOptionType) => {
                    let FilterComponent = EmptyComponent
                    if (filterComponents[filterBlock.component]) {
                      FilterComponent = filterComponents[filterBlock.component]
                    }
                    const isChecked = !!(data[filterBlock.name] && data[filterBlock.name][filterItem.key])
                    const funcData = functionalData[filterBlock.name]
                      ? functionalData[filterBlock.name][filterItem.key]
                      : {}
                    let activeValue = isChecked
                    if (filterBlock.isRadio && data[filterBlock.name]) {
                      activeValue = data[filterBlock.name][filterItem.name]
                    }
                    return (
                      <FilterComponent
                        activeValue={activeValue}
                        checked={isChecked}
                        data={funcData}
                        domain={filterItem}
                        key={`item_${filterItem.key}`}
                        onChange={(name, value) => {
                          this.handleOnChange(
                            filterBlock.name,
                            filterBlock.isRadio ? filterItem.name : filterItem.key,
                            value,
                          )
                        }}
                        parentName={filterBlock.name}
                        textMode={textMode}
                      />
                    )
                  })}
                </div>
                {showRecalculate && showRecalculateAfter && showRecalculateAfter === filterBlock.name && (
                  <div className="text-center w-100">
                    <hr className="mt-0 w-100" />
                    <button
                      className="btn btn-dark w-100 mb-3 mb-sm-0 mb-md-3 d-none d-sm-block"
                      onClick={this.handleRecalculateTab}
                      type="button"
                    >
                      {I18n.translate('general.recalculate')}
                    </button>
                  </div>
                )}
              </div>
            ),
          ]
        })}
        {showMobileFilter && !mobileFilter && isMobile() && <ScrollLock />}
        {disabled && <div className="overlay-disabled" />}
      </div>,
      !mobileFilter && (
        <CarFilter
          {...this.props}
          key="filter-mobile"
          mobileFilter
          onToggleModal={this.handleToggleShowModal}
          showFilterPush={this.state.showMobileFilter}
        />
      ),
    ]
  }
}
